import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Lightbulb, Send } from 'lucide-react';
import axios from 'axios';
import AlertItem from './AlertItem';

const AlertsPanel = ({ isDarkMode, edgeServerEndpoint, isMobile, isVisible, onClose, formatTime, timezone, timeFormat }) => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatQuestion, setChatQuestion] = useState('');
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const chatInputRef = useRef(null);
  const contentRef = useRef(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${edgeServerEndpoint}/alerts`);
      // Ensure response.data is an array
      let alertsData = [];
      
      if (response && response.data !== undefined) {
        // Handle different response structures
        if (Array.isArray(response.data)) {
          alertsData = response.data;
        } else if (response.data && Array.isArray(response.data.alerts)) {
          // If response is wrapped in an object with 'alerts' property
          alertsData = response.data.alerts;
        } else if (response.data && typeof response.data === 'object') {
          // If it's an object but not an array, log and use empty array
          console.warn('Alerts API returned non-array data:', response.data);
          alertsData = [];
        } else {
          alertsData = [];
        }
      }
      
      // Always ensure we set an array
      setAlerts(Array.isArray(alertsData) ? alertsData : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts');
      setAlerts([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [edgeServerEndpoint]);

  // Initial fetch
  useEffect(() => {
    if (isVisible) {
      fetchAlerts();
    }
  }, [isVisible, fetchAlerts]);

  // Poll for alerts every 5 seconds
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      fetchAlerts();
    }, 5000);

    return () => clearInterval(interval);
  }, [isVisible, fetchAlerts]);

  const handleDismiss = (alertId) => {
    setAlerts(prevAlerts => {
      if (!Array.isArray(prevAlerts)) return [];
      return prevAlerts.filter(alert => alert.id !== alertId);
    });
  };

  const handlePin = (alertId, pinned) => {
    setAlerts(prevAlerts => {
      if (!Array.isArray(prevAlerts)) return [];
      return prevAlerts.map(alert =>
        alert.id === alertId ? { ...alert, pinned } : alert
      );
    });
  };

  // Handle chat question submission
  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    if (!chatQuestion.trim() || isSubmittingQuestion) {
      return;
    }

    const question = chatQuestion.trim();
    setChatQuestion('');
    setIsSubmittingQuestion(true);

    // Generate a unique ID for the new insight
    const insightId = `info-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const now = new Date().toISOString();
    
    // Create a new Information insight with placeholder response
    // TODO: Replace this with actual LLM API call
    const newInsight = {
      id: insightId,
      category: 'Information',
      priority: 'Low',
      title: question,
      details: 'Response will be generated by LLM API...', // Placeholder - will be replaced with actual LLM response
      time: now,
      created_at: now,
      pinned: false,
      sticky: true, // Information insights are always sticky (no timeout)
      timeout: 0, // No timeout for Information insights
      question: question, // Store the original question
      isInformation: true // Flag to identify Information category insights
    };

    // Add the new insight to the bottom of the alerts list
    setAlerts(prevAlerts => {
      const updated = [...(Array.isArray(prevAlerts) ? prevAlerts : []), newInsight];
      return updated;
    });

    // Scroll to bottom to show the new insight
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }, 0);

    // TODO: Make API call to LLM service here
    // Example structure:
    // try {
    //   const response = await axios.post(`${edgeServerEndpoint}/insights/chat`, {
    //     question: question
    //   });
    //   // Update the insight with the response
    //   setAlerts(prevAlerts => 
    //     prevAlerts.map(alert => 
    //       alert.id === insightId 
    //         ? { ...alert, details: response.data.answer }
    //         : alert
    //     )
    //   );
    //   // Scroll to bottom to show the updated response
    //   setTimeout(() => {
    //     if (contentRef.current) {
    //       contentRef.current.scrollTop = contentRef.current.scrollHeight;
    //     }
    //   }, 0);
    // } catch (error) {
    //   console.error('Error getting LLM response:', error);
    //   setAlerts(prevAlerts => 
    //     prevAlerts.map(alert => 
    //       alert.id === insightId 
    //         ? { ...alert, details: 'Error: Could not get response from LLM service.' }
    //         : alert
    //     )
    //   );
    //   // Scroll to bottom even on error
    //   setTimeout(() => {
    //     if (contentRef.current) {
    //       contentRef.current.scrollTop = contentRef.current.scrollHeight;
    //     }
    //   }, 0);
    // }

    setIsSubmittingQuestion(false);
  };

  // Auto-scroll to bottom when new insights are added
  useEffect(() => {
    if (contentRef.current && alerts.length > 0) {
      // Scroll to bottom when new insights are added
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [alerts.length]);

  if (!isVisible) {
    return null;
  }

  // Safety check: ensure alerts is always an array
  const safeAlerts = Array.isArray(alerts) ? alerts : [];
  
  // Sort alerts: Pinned first, then by time (newest at bottom)
  const sortedAlerts = [...safeAlerts].sort((a, b) => {
    // First, sort by pinned status (pinned items first)
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    
    // If both pinned or both not pinned, sort by time (newest at bottom)
    const timeA = new Date(a.created_at || a.time || 0).getTime();
    const timeB = new Date(b.created_at || b.time || 0).getTime();
    return timeA - timeB; // Ascending order (oldest first, newest at bottom)
  });

  return (
    <div
      className={`flex flex-col h-full max-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
      } ${isMobile ? 'w-full' : 'w-full'} border-l`}
    >
      {/* Header */}
      <div
        className={`flex items-center px-4 py-3 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}
      >
        <div className="flex items-center gap-2">
          <Lightbulb
            className={`w-5 h-5 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}
          />
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Insights
          </h2>
          {sortedAlerts.length > 0 && (
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                isDarkMode
                  ? 'bg-gray-800 text-gray-300'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {sortedAlerts.length}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        ref={contentRef}
        className={`flex-1 overflow-y-auto px-4 py-3 ${
          isDarkMode ? 'dark-mode-scrollbar' : ''
        }`}
      >
        {isLoading ? (
          <div
            className={`text-center py-8 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            Loading insights...
          </div>
        ) : error ? (
          <div
            className={`text-center py-8 ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`}
          >
            {error}
          </div>
        ) : sortedAlerts.length === 0 ? (
          <div
            className={`text-center py-8 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            <Lightbulb
              className={`w-12 h-12 mx-auto mb-2 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-400'
              }`}
            />
            <p className="text-sm">No active insights</p>
          </div>
        ) : (
          <div>
            {sortedAlerts.map((alert) => {
              if (!alert || !alert.id) return null;
              return (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  isDarkMode={isDarkMode}
                  onDismiss={handleDismiss}
                  onPin={handlePin}
                  formatTime={formatTime}
                  edgeServerEndpoint={edgeServerEndpoint}
                  timezone={timezone}
                  timeFormat={timeFormat}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Chat Input Area */}
      <div
        className={`border-t px-4 py-3 ${
          isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <form onSubmit={handleSubmitQuestion} className="flex items-end gap-2">
          <div className="flex-1">
            <textarea
              ref={chatInputRef}
              value={chatQuestion}
              onChange={(e) => setChatQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitQuestion(e);
                }
              }}
              placeholder="Ask a question..."
              rows={2}
              disabled={isSubmittingQuestion}
              className={`w-full px-3 py-2 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                isDarkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                  : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
          </div>
          <button
            type="submit"
            disabled={!chatQuestion.trim() || isSubmittingQuestion}
            className={`p-2 rounded-lg transition-all duration-200 ${
              !chatQuestion.trim() || isSubmittingQuestion
                ? isDarkMode
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : isDarkMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
            }`}
            title="Send question (Enter)"
          >
            <Send 
              size={18} 
              className={isSubmittingQuestion ? 'animate-pulse' : ''}
            />
          </button>
        </form>
        <p className={`text-xs mt-2 ${
          isDarkMode ? 'text-gray-500' : 'text-gray-400'
        }`}>
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default AlertsPanel;
